import React, { Fragment, useMemo, useContext, useEffect } from "react";
import { CartContext } from "components/cart";
import classNames from "lib/classNames";
import HeaderMachine from "../machines/HeaderMachine";
import { useMachine } from "@xstate/react";
import useRefState from "hooks/useRefState";
import useCampaign from "hooks/useCampaign";

export default function HeaderATU({ add_to_unlock, bundlesDiscounts }) {
  const {
    cart_settings: {
      theme: { primary },
      font: { color },
      rtl,
      money_format
    },
    campaigns,
    setShowConfetti,
    shopify_cart_items,
    Utils,
    get_total
  } = useContext(CartContext);
  const [_, setContextCache, contextCache] = useRefState(null);
  const [one_check_up_sell_product_id, one_check_up_sell] = useCampaign(
    "one_check_up_sell"
  )(campaigns, shopify_cart_items);
  const filteredItems = shopify_cart_items.filter((item) => {
    if (
      one_check_up_sell &&
      item.product_id.toString() === one_check_up_sell?.product?.id
    ) {
      return !!one_check_up_sell?.include_in_atu;
    }
    return true;
  });
  const { original, subtotal } = get_total(null, filteredItems);
  const headerMachine = useMemo(
    () =>
      HeaderMachine({
        goals: add_to_unlock.goals,
        setShowConfetti,
        shopify_cart_items: filteredItems,
        original,
        subtotal,
        bundlesDiscounts,
        byQuantity: add_to_unlock.multi_goal_type === "quantity",
        contextCache: contextCache.current
      }),
    [shopify_cart_items, add_to_unlock, bundlesDiscounts, contextCache.current]
  );
  const [current, send, service] = useMachine(headerMachine);

  const { context } = headerMachine;

  const progressWidth = 310 / context.goals.length;

  useEffect(() => {
    if (JSON.stringify(context) !== JSON.stringify(contextCache.current)) {
      if (contextCache.current) {
        const showConfettiPreview = context?.goals?.filter(
          ({ goalObject: { confetti } }, i) =>
            confetti && !contextCache?.current?.goals[i]?.goalObject?.confetti
        );
        if (showConfettiPreview.length) {
          setShowConfetti(showConfettiPreview[0].goalObject.confetti_length);
        }
      }
      setContextCache(context);
    }
  }, [context]);

  return (
    <div
      className={classNames("px-5 py-2 mb-px shadow atu-banner")}
      data-offset={1}
      style={{ background: "inherit" }}
    >
      {context.goals.map(
        ({ textKey, goalObject, unlocked, previousIsUnlocked }, index) => (
          <GoalText
            key={textKey}
            unlocked={unlocked}
            bundlesDiscounts={bundlesDiscounts}
            currentGoal={goalObject}
            add_to_unlock={add_to_unlock}
            filteredItems={filteredItems}
            otherUnlockedDiscounts={context.goals.filter(
              ({ unlocked, goalObject: { reward_type } }, i) =>
                i !== index && unlocked && reward_type === "discount"
            )}
            previousIsUnlocked={previousIsUnlocked}
            show_all_locked_goals={add_to_unlock.show_all_locked_goals}
          />
        )
      )}
      <div
        className={classNames(
          "flex w-full h-2 rounded relative overflow-hidden mix-blend-darken"
        )}
        style={{
          background: Utils.formatColor(add_to_unlock.progress_secondary)
        }}
      >
        <div
          className={classNames(
            "block transition-width absolute inset-0 duration-500 ease-out z-10"
          )}
          style={{
            background:
              context.goals.filter(({ unlocked }) => !unlocked).length > 0
                ? Utils.formatColor(add_to_unlock.progress_primary)
                : Utils.formatColor(add_to_unlock.progress_complete),
            width: `${
              context.goals.reduce((acc, cur) => {
                const { unlocked, isFirst, previousIsUnlocked, value } = cur;
                return isFirst || previousIsUnlocked ? acc + value : acc;
              }, 0) / context.goals.length
            }%`
          }}
        />
        {context.goals.map(({ key, isFirst, isLast }, index) => (
          <ProgressBar
            key={key}
            rtl={rtl}
            isFirst={isFirst}
            isLast={isLast}
            borderColor="#718096"
          />
        ))}
      </div>
      {/* {context.goals.length > 1 && ( */}
      <div
        className={classNames(
          "flex justify-between text-gray-600 relative h-3"
        )}
      >
        {context.goals.map(
          (
            { labelKey, goalObject: { goal_text, show_goal_text }, isLast },
            index
          ) => {
            if (!show_goal_text) return null;
            return (
              <div
                key={labelKey}
                className={classNames("absolute transform w-max")}
                style={{
                  left:
                    !rtl && `${((index + 1) / context.goals.length) * 100}%`,
                  right:
                    rtl && `${((index + 1) / context.goals.length) * 100}%`,
                  width: "fit-content",
                  fontSize: Utils.em(goal_text.font_size),
                  color: Utils.formatColor(goal_text.color),
                  "--transform-translate-x": isLast ? "-100%" : "-50%"
                }}
              >
                {goal_text.text}
              </div>
            );
          }
        )}
      </div>
      {/* )} */}
    </div>
  );
}

const ProgressBar = ({ rtl, isFirst, isLast, borderColor }) => {
  return (
    <div
      className={classNames(
        "flex-1 h-2 bg-transparent z-20 mix-blend-overlay",
        {
          "border-r": !rtl && !isLast,
          "border-l": rtl && !isLast
        }
      )}
      style={{ borderColor }}
    />
  );
};

const textIsEmpty = (html) => {
  const element = document.createElement("span");
  element.innerHTML = html;
  return element.innerText.length === 0;
};

const GoalText = ({
  currentGoal,
  add_to_unlock: { multi_goal_type },
  unlocked,
  otherUnlockedDiscounts,
  previousIsUnlocked,
  show_all_locked_goals,
  bundlesDiscounts,
  filteredItems
}) => {
  const {
    cart_settings: {
      font: { color },
      money_format
    },
    Utils,
    get_total
  } = useContext(CartContext);
  const { goal, reward_type } = currentGoal;
  const reward = currentGoal[reward_type];
  const isDiscount = reward_type === "discount";
  const notTheHighestDiscount = isDiscount
    ? !!otherUnlockedDiscounts.find(
        ({ goalObject: { discount } }) => discount.reward > reward.reward
      )
    : false;
  const text = unlocked ? reward.after : reward.before;
  if (
    textIsEmpty(text) ||
    notTheHighestDiscount ||
    (previousIsUnlocked === false && !show_all_locked_goals)
  )
    return null;
  return (
    <div
      className={classNames("emoji-image mb-1 text-center")}
      style={{ color }}
      dangerouslySetInnerHTML={{
        __html: formateText(
          text,
          reward_type,
          reward,
          (v) => Utils.formatMoney(v, money_format),
          get_total, //TODO: get from context with utils
          goal,
          multi_goal_type,
          filteredItems,
          bundlesDiscounts
        )
      }}
    ></div>
  );
};

const formateText = (
  text,
  rewardType,
  { type, reward },
  formatMoney,
  get_total,
  goal,
  multi_goal_type,
  filteredItems,
  bundlesDiscounts
) => {
  let replacedReward =
    rewardType === "discount"
      ? type === "percentage"
        ? `${reward}%`
        : formatMoney(reward * 100)
      : "";
  const { subtotal, original } = get_total(null, filteredItems);
  const currentAmount =
    rewardType === "free_shipping" ? subtotal : original - bundlesDiscounts;
  let replacedAmount = formatMoney(
    (goal * 100 - currentAmount) * parseFloat(window.Shopify.currency.rate)
  );
  let replacedQuantity =
    goal -
    filteredItems
      .map((item) => item.quantity)
      .reduce((acc, item) => acc + item);

  return text
    .replace(
      "{{amount_left}}",
      multi_goal_type === "quantity" ? replacedQuantity : replacedAmount
    )
    .replace("{{reward}}", replacedReward)
    .replace("{{goal}}", formatMoney(goal * 100));
};
